/**
 * Grid Map Types
 * Self-contained types for the rectangular grid map engine.
 */

// ============================================================================
// CORE GRID
// ============================================================================

export type TerrainType = 'floor' | 'wall' | 'water' | 'door' | 'stairs_up' | 'stairs_down' | 'void';

export type FogState = 'hidden' | 'explored' | 'visible';

export type CardinalDirection = 'N' | 'E' | 'S' | 'W';

export interface Cell {
  x: number;
  y: number;
  terrain: TerrainType;
  walls: {
    N: boolean;
    E: boolean;
    S: boolean;
    W: boolean;
  };
}

export interface Entity {
  id: string;
  x: number;
  y: number;
  type: 'player' | 'npc' | 'monster' | 'object';
  name: string;
  icon?: string;
  lightRadius?: number;
  metadata?: Record<string, unknown>;
}

export interface GridMap {
  width: number;
  height: number;
  cells: Cell[][];  // [y][x]
  entities: Entity[];
  triggers?: Trigger[];
  name?: string;
  seed?: number;
  metadata?: MapMetadata;
}

// ============================================================================
// TRIGGERS
// ============================================================================

/** What activates the trigger */
export type TriggerCondition = 'step' | 'proximity' | 'visible' | 'interact';

/** Discriminated union of trigger effects */
export type TriggerEffect =
  | { type: 'dialogue'; text: string; speaker?: string }
  | { type: 'combat'; encounterId: string }
  | { type: 'trap'; damage?: number; description?: string }
  | { type: 'teleport'; targetX: number; targetY: number }
  | { type: 'spawn'; entities: Array<{ entityType: Entity['type']; name: string; x: number; y: number }> }
  | { type: 'custom'; eventName: string; data?: Record<string, unknown> };

/** Serializable trigger definition — no functions, no callbacks */
export interface Trigger {
  id: string;
  cells: Array<{ x: number; y: number }>;
  condition: TriggerCondition;
  effect: TriggerEffect;
  radius?: number;            // For proximity triggers (Manhattan distance)
  oneShot?: boolean;          // Default true — fires once then disabled
  enabled?: boolean;          // Default true
  requiredFlags?: string[];   // Only fires if all flags are set
  setsFlag?: string;          // Flag to set when fired (for chaining)
  visible?: boolean;          // Show indicator on map
  icon?: string;              // Icon character for visible triggers
  color?: string;             // Color for visible trigger icon
}

/** Event emitted when a trigger fires — consumed by the game view */
export interface TriggerEvent {
  triggerId: string;
  effect: TriggerEffect;
  sourceCell: { x: number; y: number };
  playerPosition: { x: number; y: number };
}

// ============================================================================
// RUNTIME STATE
// ============================================================================

export interface GridMapState {
  map: GridMap;
  playerId: string;
  fogGrid: FogState[][];   // [y][x]
  lightGrid: number[][];   // [y][x] 0.0–1.0
  openDoors: Set<string>;    // coordKey set of opened doors
  lockedDoors: Set<string>;  // coordKey set of locked doors (can't open or walk through)
  moveCount: number;
  firedTriggers: Set<string>;  // trigger IDs that have fired (for oneShot)
  triggerFlags: Set<string>;   // flags set by triggers (for chaining)
  pendingEvents: TriggerEvent[];  // events waiting to be consumed by the game view
}

// ============================================================================
// VISIBILITY
// ============================================================================

export interface VisibilityResult {
  visibleSet: Set<string>;
  lightValues: Map<string, number>;
}

// ============================================================================
// MOVEMENT
// ============================================================================

export type MoveDirection = 'up' | 'down' | 'left' | 'right';

// ============================================================================
// CITY DISTRICTS
// ============================================================================

export type CityDistrictType =
  | 'market' | 'residential' | 'noble' | 'temple'
  | 'waterfront' | 'slums' | 'craftsmen';

export interface CityDistrictConfig {
  type: CityDistrictType;
  weight?: number;
}

// ============================================================================
// DUNGEON THEMING
// ============================================================================

export type DungeonTheme = 'crypt' | 'fortress' | 'lair' | 'ruins' | 'sewer';

export type RoomPurpose =
  | 'entrance' | 'barracks' | 'shrine' | 'treasury' | 'library'
  | 'throne' | 'prison' | 'storage' | 'armory' | 'laboratory'
  | 'crypt' | 'empty';

export interface DungeonZone {
  id: number;
  theme: string;
  roomIds: number[];
  bounds: { x: number; y: number; width: number; height: number };
}

export interface RoomMetadata {
  roomId: number;
  purpose: RoomPurpose;
  zoneId: number;
  connections: number[];
  x: number;
  y: number;
  width: number;
  height: number;
}

// ============================================================================
// MAP METADATA
// ============================================================================

export interface DistrictMetadata {
  id: number;
  type: CityDistrictType;
  bounds: { x: number; y: number; width: number; height: number };
  buildingCount: number;
}

export interface MapMetadata {
  districts?: DistrictMetadata[];
  dungeonZones?: DungeonZone[];
  rooms?: RoomMetadata[];
}

// ============================================================================
// MAP GENERATION
// ============================================================================

export type GeneratorType = 'bsp_dungeon' | 'cave' | 'arena' | 'preset' | 'city';

export interface GeneratorConfig {
  width: number;
  height: number;
  seed: number;
  type: GeneratorType;
  // BSP
  minRoomSize?: number;
  maxRoomSize?: number;
  maxDepth?: number;
  // Cave
  fillProbability?: number;
  smoothIterations?: number;
  // Arena
  pillarSpacing?: number;
  // Preset
  presetName?: string;
  // City
  streetWidth?: number;
  blockSize?: number;
  hasCanal?: boolean;
  districts?: CityDistrictConfig[];
  // Dungeon
  dungeonTheme?: DungeonTheme;
  enableSecrets?: boolean;
  enableKeys?: boolean;
}

export interface Room {
  x: number;
  y: number;
  width: number;
  height: number;
  id: number;
}

// ============================================================================
// INPUT
// ============================================================================

export type MapInput =
  | { type: 'move'; direction: MoveDirection }
  | { type: 'interact'; x: number; y: number }
  | { type: 'toggle_door'; x: number; y: number }
  | { type: 'toggle_wall'; x: number; y: number; direction: CardinalDirection }
  | { type: 'set_terrain'; x: number; y: number; terrain: TerrainType }
  | { type: 'place_entity'; entity: Entity }
  | { type: 'remove_entity'; entityId: string };

// ============================================================================
// RENDERING
// ============================================================================

export interface RenderConfig {
  tileSize: number;
  showGrid: boolean;
  showDebugVis: boolean;
  showMinimap: boolean;
  showTriggers: boolean;
  fogEnabled: boolean;
  colors: {
    floor: string;
    wall: string;
    water: string;
    door: string;
    doorOpen: string;
    doorLocked: string;
    stairsUp: string;
    stairsDown: string;
    void: string;
    gridLine: string;
    wallEdge: string;
    fogHidden: string;
    fogExplored: string;
    playerToken: string;
    npcToken: string;
    monsterToken: string;
    objectToken: string;
  };
}

export const DEFAULT_RENDER_CONFIG: RenderConfig = {
  tileSize: 24,
  showGrid: true,
  showDebugVis: false,
  showMinimap: true,
  showTriggers: false,
  fogEnabled: true,
  colors: {
    floor: '#3a3a3a',
    wall: '#1a1a1a',
    water: '#1a3a5a',
    door: '#6b4423',
    doorOpen: '#8b6914',
    doorLocked: '#4a2020',
    stairsUp: '#4a6a4a',
    stairsDown: '#6a4a4a',
    void: '#000000',
    gridLine: '#2a2a2a',
    wallEdge: '#e0c080',
    fogHidden: '#000000',
    fogExplored: 'rgba(0, 0, 0, 0.6)',
    playerToken: '#f0c040',
    npcToken: '#40a0f0',
    monsterToken: '#f04040',
    objectToken: '#a0a0a0',
  },
};

export const DEFAULT_GENERATOR_CONFIG: GeneratorConfig = {
  width: 40,
  height: 30,
  seed: 42,
  type: 'bsp_dungeon',
  minRoomSize: 4,
  maxRoomSize: 10,
  maxDepth: 5,
  fillProbability: 0.45,
  smoothIterations: 5,
  pillarSpacing: 4,
  streetWidth: 2,
  blockSize: 10,
  hasCanal: false,
};
